import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChildModal, ComponentLoader, useModal } from 'cat-ecommerce-alloy';
import ImportSavedListModalContent from '@app/components/common/ProductForm/ImportSavedListModalContent';
import ProductForm from '@app/components/common/ProductForm';
import QuickOrderActionButtons from '../QuickOrderActionButtons';
import PropTypes from 'prop-types';
import { useWebStorage } from '@app/hooks';
import { CAT_IMPORT_LIST_FLAG, STATUS } from '@app/constants/commonConstants';
import { removeQuickOrderCookie } from '../utils';

const QuickOrderMobileModal = ({
  errorInfo,
  initialItems,
  itemNumberValidator,
  maxRows,
  mobileModalOptions,
  quantityValidator,
  shoppingCartMaxItems,
  onAddToCartClick
}) => {
  const [t] = useTranslation();
  const { isOpen, open, close } = mobileModalOptions;
  const importSavedListChildModalOptions = useModal({
    isChild: true,
    parentIsOpen: isOpen
  });
  const { close: savedListClose } = importSavedListChildModalOptions;
  const sosItemDetailsStatus = useSelector(
    s => s.checkout?.sosItemDetailsStatus
  );
  const isSosItemDetailsLoading = sosItemDetailsStatus === STATUS.PENDING;
  const dealerPriceAndAvailabilityStatus = useSelector(
    s => s.checkout.dealerPriceAndAvailabilityStatus
  );
  const isDealerPriceAndAvailabilityLoading =
    dealerPriceAndAvailabilityStatus === STATUS.PENDING;
  const addToCartStatus = useSelector(s => s.checkout.addToCartStatus);
  const isAddToCartLoading = addToCartStatus === STATUS.PENDING;
  const addToCartSubmitStatus = useSelector(
    s => s.checkout.addToCartSubmitStatus
  );
  const isAddToCartBtnLoading = addToCartSubmitStatus === STATUS.PENDING;
  const isOrderPriceAvailabilityLoading = useSelector(
    s => s.orders.isOrderPriceAvailabilityLoading
  );

  const [importSavedListFlag, setImportSavedListFlag] = useWebStorage(
    CAT_IMPORT_LIST_FLAG,
    'session'
  );

  useEffect(() => {
    if (importSavedListFlag === true) {
      open();
      setImportSavedListFlag(undefined);
    }
    return () => {
      setImportSavedListFlag(undefined);
    };
  }, [open, setImportSavedListFlag, importSavedListFlag]);

  return (
    <>
      {(isSosItemDetailsLoading ||
        isAddToCartBtnLoading ||
        isAddToCartLoading ||
        isOrderPriceAvailabilityLoading ||
        isDealerPriceAndAvailabilityLoading) && (
        <ComponentLoader contentClassName="position-sticky" />
      )}
      <ProductForm
        errorInfo={errorInfo}
        initialItems={initialItems}
        clearButtonClassName="ps-0 align-self-start"
        handleOpenImportChild={importSavedListChildModalOptions.open}
        itemNumberMaxLength={20}
        itemNumberValidator={itemNumberValidator}
        isWithStickyActionButtons
        maxRows={maxRows}
        onClearAll={removeQuickOrderCookie}
        quantityMaxLength={5}
        quantityValidator={quantityValidator}
        shoppingCartMaxItems={shoppingCartMaxItems}
        withCopyPastePopup
        withFileUpload
      />
      <QuickOrderActionButtons
        close={close}
        onAddToCartClick={onAddToCartClick}
      />
      <ChildModal
        {...importSavedListChildModalOptions}
        backButtonMessage={t('BACK_TO_QUICK_ORDER')}
        title={t('IMPORT_SAVED_LIST')}
      >
        <ImportSavedListModalContent close={savedListClose} isOpen={isOpen} />
      </ChildModal>
    </>
  );
};

QuickOrderMobileModal.propTypes = {
  childModalOptions: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  }),
  mobileModalOptions: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  }),
  itemNumberValidator: PropTypes.func,
  quantityValidator: PropTypes.func,
  maxRows: PropTypes.number,
  shoppingCartMaxItems: PropTypes.number,
  initialItems: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.string,
      itemNumber: PropTypes.string,
      assetId: PropTypes.string,
      lineItemNote: PropTypes.string,
      customerPartNumber: PropTypes.string,
      customerItemNumber: PropTypes.string,
      id: PropTypes.string
    })
  ),
  defaultRowValues: PropTypes.shape({
    quantity: PropTypes.string,
    itemNumber: PropTypes.string,
    assetId: PropTypes.string,
    lineItemNote: PropTypes.string,
    customerPartNumber: PropTypes.string,
    customerItemNumber: PropTypes.string
  }),
  errorInfo: PropTypes.shape({
    domain: PropTypes.string,
    path: PropTypes.string
  }),
  isAvailabilityToggled: PropTypes.bool,
  setIsAvailabilityToggled: PropTypes.func,
  onAddToCartClick: PropTypes.func
};

export default QuickOrderMobileModal;
